import { BaseComponent, Splide, Components, Options } from '@splidejs/splide';

/**
 * Declare options to splide lib for compiler
 */
declare module '@splidejs/splide' {
  interface Options {
    focusColor?: FocusColorOptions | undefined;
  }
}

/**
 * Declare extension options
 */
export interface FocusColorOptions {
  focusIndex?: number;
}

/**
 * Focus color extension
 *
 * @param Splide     - Splide instance
 * @param Components - Collection of splide components
 * @param options    - Options
 */
export default function FocusColorExtension(
  Splide: Splide,
  Components: Components,
  options: Options
): BaseComponent {
  const { getEnd, setIndex } = Components.Controller;
  const { reposition } = Components.Move;

  const perPage: number | undefined = options.perPage;

  let focusColorOptions: FocusColorOptions | undefined;

  /**
   * Sets up the component.
   */
  function setup() {
    const { focusColor } = options;
    focusColorOptions = focusColor;
  }

  /**
   * Called when the component is mounted.
   */
  function mount() {
    const limitScroll = getEnd();
    const focusIndex = focusColorOptions?.focusIndex;
    if (undefined === focusIndex || undefined === perPage) {
      return;
    }

    const goToIndex = getFocusIndex(focusIndex, limitScroll);

    setIndex(goToIndex);
    reposition();
  }

  /**
   * Calculated the focus in the slider
   *
   * @param focusIndexOption - The value of focusIndex option
   * @param limitScroll      - Index limit in slider
   *                           (with splide, the limit is the last element on the left that can be visible)
   */
  function getFocusIndex(
    focusIndexOption: number,
    limitScroll: number
  ): number {
    return Math.min(
      Math.max(focusIndexOption - Math.ceil(perPage / 2) + 1, 0),
      limitScroll
    );
  }

  return {
    setup,
    mount,
  };
}
